
import React from "react";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SectionRenderer from "./SectionRenderer";

interface SOAPData {
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  diagnosis?: string;
}

interface SOAPTemplateProps {
  data: SOAPData;
  onDataChange: (updatedData: Partial<SOAPData>) => void;
  dictationPhase: 'general' | 'refine';
  className?: string;
}

const SOAPTemplate: React.FC<SOAPTemplateProps> = ({
  data = {},
  onDataChange,
  dictationPhase,
  className = "",
}) => {
  const { toast } = useToast();

  const handleCopyAllNotes = () => {
    const allContent = [
      data.subjective && `Subjective: ${data.subjective}`,
      data.objective && `Objective: ${data.objective}`,
      data.assessment && `Assessment: ${data.assessment}`,
      data.plan && `Plan: ${data.plan}`
    ].filter(Boolean).join('\n\n');
    
    if (allContent.trim()) {
      navigator.clipboard.writeText(allContent).then(
        () => {
          toast({
            title: "Copied!",
            description: "All SOAP notes copied to clipboard."
          });
        },
        () => {
          toast({
            title: "Copy Failed",
            variant: "destructive",
            description: "Failed to copy notes to clipboard."
          });
        }
      );
    } else {
      toast({
        title: "No Content",
        description: "No notes to copy.",
        variant: "destructive"
      });
    }
  };
  
  const hasAnyContent = Object.values(data).some(value => !!value);

  return (
    <div className={`bg-white ${className}`}>
      {/* Mobile-optimized header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 pb-3 sm:pb-4 border-b border-gray-200 px-3 sm:px-4">
        <div className="mb-2 sm:mb-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">SOAP Notes</h2>
        </div>
        
        {/* Copy button - only show if there's content and in refine phase */}
        {hasAnyContent && dictationPhase === 'refine' && (
          <Button
            onClick={handleCopyAllNotes}
            variant="outline"
            size="sm"
            className="w-full sm:w-auto mobile-touch-target"
          >
            <Copy className="h-4 w-4 mr-2" />
            <span className="text-sm">Copy All</span>
          </Button>
        )}
      </div>

      {/* Mobile-optimized sections */}
      <div className="space-y-0 divide-y divide-gray-100">
        <SectionRenderer
          sectionId="subjective"
          title="Subjective"
          value={data.subjective || ''}
          onValueChange={(value) => onDataChange({ subjective: value })}
          placeholder="Patient reports, symptoms, chief complaint..."
          dictationPhase={dictationPhase}
        />
        <SectionRenderer
          sectionId="objective"
          title="Objective"
          value={data.objective || ''}
          onValueChange={(value) => onDataChange({ objective: value })}
          placeholder="Vital signs, physical examination findings..."
          dictationPhase={dictationPhase}
        />
        <SectionRenderer
          sectionId="assessment"
          title="Assessment"
          value={data.assessment || ''}
          onValueChange={(value) => onDataChange({ assessment: value })}
          placeholder="Clinical impression, differential diagnosis..."
          dictationPhase={dictationPhase}
        />
        <SectionRenderer
          sectionId="plan"
          title="Plan"
          value={data.plan || ''}
          onValueChange={(value) => onDataChange({ plan: value })}
          placeholder="Treatment plan, medications, follow-up instructions..."
          dictationPhase={dictationPhase}
        />
      </div>
    </div>
  );
};

export default SOAPTemplate;
